import { TaskType } from '../api/task-types/task-main.js';
import { flattenExamTasks, flattenSubmissionTasks } from './submission-tree.js';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type TaskComparisonMode = 'discrete' | 'freeText' | 'numeric' | 'unsupported';

export interface TaskSimilarityDetail {
  taskNumber: string;
  taskType: TaskType;
  studentAAnswer: string | null;
  studentBAnswer: string | null;
  rawSimilarity: number;
  surprisalWeight: number;
  wrongAnswerBoost: number;
  weightedScore: number;
  /** Both answered (near-)identically, both lost most of the points, and the class doesn't commonly give that answer. */
  isWrongForBoth: boolean;
  /** Long free-text answers that match (near-)word-for-word and aren't shared by most of the class. */
  isVerbatim: boolean;
}

/** Why a pair was flagged, strongest evidence first. `statistical` is the only rule relative to the class. */
export type CopyFlagReason = 'identicalSubmission' | 'verbatimText' | 'identicalAnswers' | 'statistical';

export interface PairwiseCopyScore {
  /** Actually the two submissions' `_id`s, not `Submission.studentID` — see `buildAnswersByStudent`. Named
   *  `studentId*` because that's the concept ("which student"); a submission stands in one-to-one for a
   *  student here, and the UI already has `_id -> studentName` from the Summary canvas's own submission list. */
  studentIdA: string;
  studentIdB: string;
  overallScore: number;
  /** Number of tasks BOTH students answered and were comparable on — the divisor of `overallScore`. Larger
   *  than `perTask.length`, which only lists tasks where a non-trivial match was actually found. */
  taskCount: number;
  perTask: TaskSimilarityDetail[];
  /** Robust z-score (median/MAD) within this exam's pairwise distribution. */
  zScore: number;
  /** Word-trigram similarity of both submissions' written-out answers taken as one text, in task order, leaving out
   *  trigrams most of the class wrote; null when either has too little text of its own for the comparison to mean
   *  anything. */
  submissionSimilarity: number | null;
  flagged: boolean;
  flagReason: CopyFlagReason | null;
}

export interface CopyDetectionResult {
  examId: string;
  computedAt: string;
  submissionCount: number;
  pairs: PairwiseCopyScore[];
  flaggedPairIds: string[];
  meanScore: number;
  stdDevScore: number;
  excludedTaskNumbers: string[];
  /** Tasks that were actually compared — empty means nothing could be checked, not that nothing was found. */
  checkedTaskNumbers: string[];
}

export interface CopyDetectionOptions {
  /** z-score above which a pair is flagged as suspicious, relative to this exam's own pairwise distribution. */
  zScoreThreshold?: number;
  /** Below this fraction of `declaredMaxPoints`, an answer counts as "wrong" for the collusion boost. */
  wrongThreshold?: number;
  /** Multiplier applied to the surprisal weight when both students' shared answer is wrong. */
  wrongAnswerBoostFactor?: number;
  /** Word-trigram similarity above which two free-text answers are folded into the same frequency cluster. */
  freeTextClusterThreshold?: number;
  /** Word-trigram similarity at or above which a long free-text match counts as verbatim copying. */
  verbatimSimilarityThreshold?: number;
  /** Both answers need at least this many words for a free-text match to count as verbatim. */
  verbatimMinTokens?: number;
  /** Minimum comparable tasks for the "identical on every task" rule. */
  identicalMinTasks?: number;
  /** The statistical rule needs at least this many pairs — below it a single outlier can't reach any z-threshold. */
  statisticalMinPairs?: number;
  /** Chance per exam that the statistical rule flags a pair of students who worked alone (Bonferroni over all pairs). */
  statisticalSignificance?: number;
  /** Whole-submission similarity at or above which two submissions count as the same submission. */
  submissionSimilarityThreshold?: number;
  /** Lower whole-submission threshold that applies when the pair also shares at least one wrong answer. */
  submissionSimilarityWithMistakeThreshold?: number;
  /** Minimum written-out words per submission, beyond what most of the class wrote too, before the whole-submission
   *  comparison applies. */
  submissionMinTokens?: number;
}

const DEFAULT_OPTIONS: Required<CopyDetectionOptions> = {
  zScoreThreshold: 3,
  wrongThreshold: 0.5,
  wrongAnswerBoostFactor: 1.75,
  freeTextClusterThreshold: 0.6,
  verbatimSimilarityThreshold: 0.7,
  verbatimMinTokens: 15,
  identicalMinTasks: 4,
  statisticalMinPairs: 10,
  statisticalSignificance: 0.05,
  submissionSimilarityThreshold: 0.85,
  submissionSimilarityWithMistakeThreshold: 0.7,
  submissionMinTokens: 40,
};

/** Only answers at least this long feed the whole-submission text: one-word and multiple-choice answers would
 *  make two students who both got everything right look like one submission. */
const WRITTEN_ANSWER_MIN_TOKENS = 6;

/** Raw similarity at or above which two answers on a task count as the same answer. */
const NEAR_IDENTICAL_SIMILARITY = 0.9;

const IDENTICAL_TASK_SHARE = 0.9;
/** In an easy exam most pairs are near-identical anyway, so one shared mistake is a coincidence two honest students
 *  can have — two uncommon ones aren't. One counts only when the match as a whole is improbable for a student working
 *  alone (below this chance), as being identical nearly everywhere is in a harder exam. */
const IDENTICAL_MIN_SHARED_MISTAKES = 2;
const IDENTICAL_SINGLE_MISTAKE_CHANCE = 0.01;

/** Shared by at least this many students AND by at least `COMMON_MATERIAL_SHARE` of the class, a verbatim text
 *  is more likely quoted source material or a dictated model answer than copying. */
const COMMON_MATERIAL_MIN_STUDENTS = 3;
const COMMON_MATERIAL_SHARE = 0.5;
/** A wrong answer given by at least `COMMON_MATERIAL_MIN_STUDENTS` and this share of the class is a common
 *  misconception, not a fingerprint two students can only share by copying. */
const COMMON_MISTAKE_SHARE = 0.2;

const SHINGLE_SIZE = 3;

/** Bounds the stored result document — every compared subtask of every flagged pair carries both answers. */
const MAX_STORED_ANSWER_CHARS = 1000;

/** A SHORT_TEXT answer this long behaves like free text, not a one-word/one-value response. */
const SHORT_TEXT_WORD_COUNT_ESCALATION = 8;

// ---------------------------------------------------------------------------
// Task-type classification
// ---------------------------------------------------------------------------

/**
 * How a task's `studentAnswer` should be compared, derived from `taskType` alone — there is no stored
 * complexity/difficulty field anywhere in the schema, so this classification IS the complexity signal.
 *
 * `Record<TaskType, ...>` rather than a switch/default: every new TaskType must be classified explicitly
 * (compiler-enforced), so an uncategorized future type fails to build instead of silently defaulting.
 */
const COMPARISON_MODE_BY_TASK_TYPE: Record<TaskType, TaskComparisonMode> = {
  [TaskType.TASK_FRAME]: 'unsupported', // structural container, never itself answered
  [TaskType.DEFAULT]: 'freeText',
  [TaskType.SHORT_TEXT]: 'discrete', // escalated to freeText per-task if answers run long, see resolveEffectiveMode
  [TaskType.ESSAY]: 'freeText',
  [TaskType.DIAGRAM]: 'unsupported', // graphical, no OCR'd answer text
  [TaskType.SKETCH]: 'unsupported',
  [TaskType.GAP_TEXT]: 'discrete',
  [TaskType.LABEL_IMAGE]: 'discrete', // labels are short text, even though attached to an image
  [TaskType.MULTIPLE_CHOICE]: 'discrete',
  [TaskType.IMAGE_ANALYSIS]: 'unsupported',
  [TaskType.MAPPING]: 'discrete',
  [TaskType.LEGAL_CASE]: 'freeText',
  [TaskType.CALCULATION]: 'numeric',
  [TaskType.MATHEMATICAL_PROOF]: 'freeText', // a proof is argued in prose; its first number says nothing
  [TaskType.CURVE_ANALYSIS]: 'unsupported', // graphical construct
  [TaskType.PLOTTING]: 'unsupported',
  [TaskType.GEOMETRY]: 'unsupported',
  [TaskType.PROGRAMMING]: 'unsupported', // output is graded behaviorally, not by an answer-text comparison
  [TaskType.ART]: 'unsupported',
  [TaskType.DICTATION]: 'unsupported', // audio-graded
  [TaskType.TRANSLATION]: 'freeText',
  [TaskType.CHEMICAL_FORMULA]: 'discrete',
  [TaskType.MUSIC]: 'unsupported',
  [TaskType.LITERARY_ANALYSIS_POETRY]: 'freeText',
  [TaskType.ANALYSIS_NON_FICTION]: 'freeText',
  [TaskType.MATERIAL_BASED_ESSAY]: 'freeText',
};

export function classifyTaskComparisonMode(taskType: TaskType): TaskComparisonMode {
  return COMPARISON_MODE_BY_TASK_TYPE[taskType] ?? 'unsupported';
}

/** SHORT_TEXT covers both one-word and short-paragraph answers, and a CALCULATION answer is either a bare result or
 *  a whole worked solution. The long variants are compared as text (word trigrams) rather than by one value. */
function resolveEffectiveMode(baseMode: TaskComparisonMode, taskType: TaskType, answers: string[]): TaskComparisonMode {
  const escalates =
    (baseMode === 'discrete' && taskType === TaskType.SHORT_TEXT) || (baseMode === 'numeric' && taskType === TaskType.CALCULATION);
  if (!escalates) return baseMode;
  const maxWordCount = answers.reduce((max, answer) => Math.max(max, tokenize(answer).length), 0);
  return maxWordCount > SHORT_TEXT_WORD_COUNT_ESCALATION ? 'freeText' : baseMode;
}

// ---------------------------------------------------------------------------
// Answer normalization & comparators
// ---------------------------------------------------------------------------

/** Two transcriptions of the same handwriting must not diverge on notation: NFKC folds "x²" into "x2", "^"/"_" and
 *  braces are dropped so "x^{2}" matches it, "\frac{x}{2}" becomes "x/2", "\cdot"/"\times" become "*", and other
 *  LaTeX command names go. A leading subtask label ("a)", "(c)") is dropped too — some transcriptions include it,
 *  some don't — unless it is the whole answer. */
function normalizeAnswer(answer: string): string {
  let text = answer.normalize('NFKC');
  // Innermost fraction first, so a nested one unwraps over a few passes.
  for (let pass = 0; pass < 3 && text.includes('frac'); pass++) {
    text = text.replace(/\\[dt]?frac\s*\{([^{}]*)\}\s*\{([^{}]*)\}/g, '$1/$2');
  }
  return text
    .replace(/\\(?:cdot|times)(?![a-zA-Z])|[·×]/g, '*')
    .replace(/\\[a-zA-Z]+/g, ' ')
    .replace(/[\^_{}$]/g, '')
    .trim()
    .toLowerCase()
    .replace(/^\(?[a-h]\)\s*(?=\S)/, '')
    .replace(/\s+/g, ' ');
}

/** Unicode-aware word tokenizer so German umlauts/ß aren't split apart. */
function tokenize(answer: string): string[] {
  return normalizeAnswer(answer).match(/[\p{L}\p{N}]+/gu) ?? [];
}

/** A multiple-choice answer is transcribed as a checklist of every option, ticked or not ("- [x] A) Text"). Only the
 *  ticks are the answer: the unticked options read the same for everyone, and OCR noise in their text must not make
 *  two identical choices look different. A ticked option is identified by its label ("a)") when it has one.
 *  Null when the answer isn't a checklist. */
function selectedChecklistOptions(answer: string): string[] | null {
  const lines = answer.split('\n').map(line => line.match(/^\s*[-*]\s*\[([ xX]?)\]\s*(.*)$/));
  if (!lines.some(Boolean)) return null;
  const selected = lines
    .filter((match): match is RegExpMatchArray => match?.[1]?.toLowerCase() === 'x')
    .map(match => {
      const option = match[2] ?? '';
      const label = option.match(/^\(?([a-z]|\d{1,2})[).:]/i)?.[1];
      return label ? label.toLowerCase() : tokenize(option).join(' ');
    });
  return [...new Set(selected)].sort();
}

/** A checklist with nothing ticked is an unanswered multiple-choice task, not an answer two students can share. */
function hasAnswer(answer: string | null | undefined): answer is string {
  return !!answer?.trim() && selectedChecklistOptions(answer)?.length !== 0;
}

/** What "the same answer" means for a one-value answer: the ticked options of a checklist, else the normalized text
 *  with all whitespace removed ("x = 9" is "x=9", "2 H2O" is "2H2O"). */
function discreteKey(answer: string): string {
  const selected = selectedChecklistOptions(answer);
  return selected ? `[${selected.join('|')}]` : normalizeAnswer(answer).replace(/\s+/g, '');
}

/** Case/whitespace-insensitive exact match — for answers that are either the same choice or not. */
export function discreteSimilarity(answerA: string, answerB: string): number {
  return discreteKey(answerA) === discreteKey(answerB) ? 1 : 0;
}

function jaccard(setA: Set<string>, setB: Set<string>): number {
  if (setA.size === 0 || setB.size === 0) return 0;
  let intersectionSize = 0;
  for (const item of setA) if (setB.has(item)) intersectionSize += 1;
  return intersectionSize / (setA.size + setB.size - intersectionSize);
}

function shingles(tokens: string[]): Set<string> {
  const result = new Set<string>();
  for (let i = 0; i + SHINGLE_SIZE <= tokens.length; i++) result.add(tokens.slice(i, i + SHINGLE_SIZE).join(' '));
  return result;
}

/** Jaccard over word trigrams: order-aware, so it measures copied wording rather than shared topic vocabulary
 *  (two independent essays on the same topic share most words but few three-word sequences). Answers too short
 *  for trigrams fall back to plain word-set Jaccard. */
export function lexicalSimilarity(answerA: string, answerB: string): number {
  const tokensA = tokenize(answerA);
  const tokensB = tokenize(answerB);
  if (tokensA.length < SHINGLE_SIZE || tokensB.length < SHINGLE_SIZE) {
    return jaccard(new Set(tokensA), new Set(tokensB));
  }
  return jaccard(shingles(tokensA), shingles(tokensB));
}

const SUPERSCRIPT_CHARS = '⁰¹²³⁴⁵⁶⁷⁸⁹⁻⁺';
const SUPERSCRIPT_ASCII = '0123456789-+';

/** A number, read with what makes it a value: a fraction bar ("3/4"), a power of ten ("6.02*10^{23}"), or being a
 *  power of ten itself ("10^{-3}"). Not after a letter, digit or bracket — "cm2", "x1" and "(x+1)2" are exponents
 *  and indices, not values. */
const NUMBER_PATTERN =
  /(?<![\p{L}\p{N}).^])(-?\d+(?:\.\d+)?)(?:\s*[/⁄]\s*(\d+(?:\.\d+)?)|\s*[*·×]\s*10\s*\^\s*\{?\s*(-?\d+)\s*\}?|\s*\^\s*\{?\s*(-?\d+)\s*\}?)?/gu;

/** The LAST number: in "2x = 18, x = 9" the result is 9, the first number is just where the working started. An
 *  exponent or index is never the result — in "9,81 m/s²" it is 9.81, not 2 — except a power of ten's, so
 *  "6,02 · 10²³" is 6.02e23 rather than 10. */
function extractNumber(answer: string): number | null {
  const text = answer
    // Before NFKC, which would fold "²" into a plain "2" that reads like a coefficient.
    .replace(
      /[⁰¹²³⁴⁵⁶⁷⁸⁹⁻⁺]+/g,
      sup => `^{${[...sup].map(c => SUPERSCRIPT_ASCII[SUPERSCRIPT_CHARS.indexOf(c)]).join('')}}`,
    )
    .replace(/[₀₁₂₃₄₅₆₇₈₉]+/g, '')
    .normalize('NFKC')
    .replace(/\\[dt]?frac\s*\{([^{}]*)\}\s*\{([^{}]*)\}/g, '$1/$2')
    .replace(/\\(?:cdot|times)(?![a-zA-Z])/g, '*')
    .replace(/[−–]/g, '-')
    .replace(/,/g, '.')
    // Every exponent but a power of ten's, and every index.
    .replace(/(?<!(?:^|[^\d.])10\s*)\^\s*(?:\{[^{}]*\}|-?\d+(?:\.\d+)?|\S)|_\s*(?:\{[^{}]*\}|\S)/g, '');
  let value: number | null = null;
  for (const [, number, denominator, scientificExponent, powerExponent] of text.matchAll(NUMBER_PATTERN)) {
    value = Number(number);
    if (denominator && Number(denominator) !== 0) value /= Number(denominator);
    if (scientificExponent) value *= 10 ** Number(scientificExponent);
    if (powerExponent) value **= Number(powerExponent);
  }
  return value;
}

/** Best-effort numeric comparison with a relative tolerance; falls back to exact-string match when no number
 *  can be extracted from either answer (e.g. a symbolic/formula answer rather than a bare value). Relative all the
 *  way down: an absolute floor would make every small value equal ("1,6 · 10⁻¹⁹" and "3,2 · 10⁻¹⁹", "0,002" and "0,009"). */
export function numericSimilarity(answerA: string, answerB: string, tolerance = 0.01): number {
  const numberA = extractNumber(answerA);
  const numberB = extractNumber(answerB);
  if (numberA === null || numberB === null) return discreteSimilarity(answerA, answerB);
  const scale = Math.max(Math.abs(numberA), Math.abs(numberB));
  if (scale === 0) return 1;
  return Math.abs(numberA - numberB) / scale <= tolerance ? 1 : 0;
}

function similarityForMode(mode: TaskComparisonMode, answerA: string, answerB: string): number {
  switch (mode) {
    case 'discrete':
      return discreteSimilarity(answerA, answerB);
    case 'freeText':
      return lexicalSimilarity(answerA, answerB);
    case 'numeric':
      return numericSimilarity(answerA, answerB);
    case 'unsupported':
      return 0;
  }
}

// ---------------------------------------------------------------------------
// Surprisal weighting & wrong-answer boost
// ---------------------------------------------------------------------------

/** -log(p): rarer matches (within this exam's own class) score higher, common ones (e.g. the obvious multiple-
 *  choice answer) score near zero. This is what stands in for a complexity field the schema doesn't have. */
export function computeSurprisalWeight(
  answerKey: string,
  frequencyByAnswer: Map<string, number>,
  totalStudents: number,
): number {
  if (totalStudents <= 1) return 0;
  const count = frequencyByAnswer.get(answerKey) ?? 1;
  const probability = Math.max(count, 1) / totalStudents;
  return -Math.log(probability);
}

export function computeWrongAnswerBoost(
  scoreA: number | null,
  scoreB: number | null,
  declaredMaxPoints: number | null | undefined,
  wrongThreshold = DEFAULT_OPTIONS.wrongThreshold,
  boostFactor = DEFAULT_OPTIONS.wrongAnswerBoostFactor,
): number {
  if (!declaredMaxPoints || declaredMaxPoints <= 0 || scoreA === null || scoreB === null) return 1;
  const ratioA = scoreA / declaredMaxPoints;
  const ratioB = scoreB / declaredMaxPoints;
  return ratioA < wrongThreshold && ratioB < wrongThreshold ? boostFactor : 1;
}

/** Union-find over answers that count as "the same answer" for frequency purposes — a close free-text paraphrase,
 *  or one value written two ways ("x = 9", "9,0") — otherwise every variant is unique, surprisal is always maximal,
 *  and it stops meaning anything. */
function clusterAnswers(uniqueAnswers: string[], isSameAnswer: (a: string, b: string) => boolean): Map<string, string> {
  const parent = new Map(uniqueAnswers.map(answer => [answer, answer]));
  const find = (answer: string): string => {
    let root = answer;
    while (parent.get(root) !== root) root = parent.get(root) as string;
    let current = answer;
    while (parent.get(current) !== root) {
      const next = parent.get(current) as string;
      parent.set(current, root);
      current = next;
    }
    return root;
  };
  const union = (a: string, b: string): void => {
    const rootA = find(a);
    const rootB = find(b);
    if (rootA !== rootB) parent.set(rootA, rootB);
  };

  for (let i = 0; i < uniqueAnswers.length; i++) {
    const answerI = uniqueAnswers[i] as string;
    for (let j = i + 1; j < uniqueAnswers.length; j++) {
      const answerJ = uniqueAnswers[j] as string;
      if (isSameAnswer(answerI, answerJ)) {
        union(answerI, answerJ);
      }
    }
  }

  const clusterKey = new Map<string, string>();
  for (const answer of uniqueAnswers) clusterKey.set(answer, find(answer));
  return clusterKey;
}

/** Builds the "how common is this answer in the class" frequency table used for surprisal, plus the per-answer
 *  cluster key to look it up by. "The same answer" is what the pairwise comparison counts as a match — otherwise
 *  "x = 9" and "9" would match each other yet each look rare. */
function buildFrequencyTable(
  mode: TaskComparisonMode,
  answers: string[],
  freeTextClusterThreshold: number,
): { frequencyByAnswer: Map<string, number>; clusterKeyByAnswer: Map<string, string> } {
  const uniqueAnswers = [...new Set(answers)];
  const clusterKeyByAnswer =
    mode === 'freeText'
      ? clusterAnswers(uniqueAnswers, (a, b) => lexicalSimilarity(a, b) >= freeTextClusterThreshold)
      : mode === 'numeric'
        ? clusterAnswers(uniqueAnswers, (a, b) => numericSimilarity(a, b) === 1)
        : new Map(uniqueAnswers.map(answer => [answer, discreteKey(answer)]));

  const frequencyByAnswer = new Map<string, number>();
  for (const answer of answers) {
    const key = clusterKeyByAnswer.get(answer) ?? answer;
    frequencyByAnswer.set(key, (frequencyByAnswer.get(key) ?? 0) + 1);
  }
  return { frequencyByAnswer, clusterKeyByAnswer };
}

// ---------------------------------------------------------------------------
// Per-task pairwise scoring
// ---------------------------------------------------------------------------

export interface StudentTaskAnswer {
  answer: string | null;
  actualPointsGiven: number | null;
}

export interface TaskPairwiseContribution {
  studentIdA: string;
  studentIdB: string;
  rawSimilarity: number;
  weightedScore: number;
  /** One per compared subtask, matching or not — a flagged pair is shown subtask by subtask. */
  detail: TaskSimilarityDetail;
  chance: ChanceMatch;
}

/** One task's input to the statistical rule's significance test. */
export interface ChanceMatch {
  /** Both answers fall in the same frequency cluster — the same choice, value, or close paraphrase. */
  isSameAnswer: boolean;
  /** How likely another student working alone gives A's (resp. B's) answer. */
  probabilityA: number;
  probabilityB: number;
}

function pairKey(studentIdA: string, studentIdB: string): [string, string] {
  return studentIdA < studentIdB ? [studentIdA, studentIdB] : [studentIdB, studentIdA];
}

/** Full per-task pipeline: classifies the task, compares every pair of students who answered it, and weights
 *  each match by how surprising it is within this class plus a boost when the shared answer is wrong. */
export function computeTaskPairwiseScores(
  task: { taskNumber: string; taskType: TaskType; declaredMaxPoints?: number | null },
  answersByStudent: Map<string, StudentTaskAnswer>,
  options: CopyDetectionOptions = {},
): TaskPairwiseContribution[] {
  const opts = { ...DEFAULT_OPTIONS, ...options };
  const baseMode = classifyTaskComparisonMode(task.taskType);
  if (baseMode === 'unsupported') return [];

  const answered = [...answersByStudent.entries()]
    .map(([studentId, data]): [string, StudentTaskAnswer & { answer: string }] | null =>
      hasAnswer(data.answer) ? [studentId, { ...data, answer: data.answer.trim() }] : null,
    )
    .filter(entry => entry !== null);
  if (answered.length < 2) return [];

  const mode = resolveEffectiveMode(
    baseMode,
    task.taskType,
    answered.map(([, a]) => a.answer),
  );
  const { frequencyByAnswer, clusterKeyByAnswer } = buildFrequencyTable(
    mode,
    answered.map(([, a]) => a.answer),
    opts.freeTextClusterThreshold,
  );
  const totalStudents = answered.length;

  const contributions: TaskPairwiseContribution[] = [];
  for (let i = 0; i < answered.length; i++) {
    for (let j = i + 1; j < answered.length; j++) {
      const [studentIdA, dataA] = answered[i] as [string, StudentTaskAnswer & { answer: string }];
      const [studentIdB, dataB] = answered[j] as [string, StudentTaskAnswer & { answer: string }];
      const rawSimilarity = similarityForMode(mode, dataA.answer, dataB.answer);

      const clusterKeyA = clusterKeyByAnswer.get(dataA.answer) ?? dataA.answer;
      const clusterKeyB = clusterKeyByAnswer.get(dataB.answer) ?? dataB.answer;
      const clusterSizeA = frequencyByAnswer.get(clusterKeyA) ?? 1;
      const clusterSizeB = frequencyByAnswer.get(clusterKeyB) ?? 1;
      // A partial free-text match can span two clusters: the more common one decides, so the score is the same
      // whichever student happens to be listed first.
      const clusterKey = clusterSizeB > clusterSizeA ? clusterKeyB : clusterKeyA;
      const clusterSize = Math.max(clusterSizeA, clusterSizeB);
      const isSameAnswer = clusterKeyA === clusterKeyB;
      const surprisalWeight = rawSimilarity > 0 ? computeSurprisalWeight(clusterKey, frequencyByAnswer, totalStudents) : 0;
      const wrongAnswerBoost = computeWrongAnswerBoost(
        dataA.actualPointsGiven,
        dataB.actualPointsGiven,
        task.declaredMaxPoints,
        opts.wrongThreshold,
        opts.wrongAnswerBoostFactor,
      );
      const weightedScore = rawSimilarity * surprisalWeight * wrongAnswerBoost;
      const isSharedByMany = (share: number) =>
        clusterSize >= COMMON_MATERIAL_MIN_STUDENTS && clusterSize / totalStudents >= share;
      const isCommonMaterial = isSharedByMany(COMMON_MATERIAL_SHARE);
      // Prose only: a worked calculation compared as text is formulaic — every correct solution of "5x − 7 = 3x + 11"
      // reads alike, so one matching calculation proves nothing on its own.
      const isVerbatim =
        mode === 'freeText' &&
        baseMode !== 'numeric' &&
        rawSimilarity >= opts.verbatimSimilarityThreshold &&
        Math.min(tokenize(dataA.answer).length, tokenize(dataB.answer).length) >= opts.verbatimMinTokens &&
        !isCommonMaterial;

      const [sortedA, sortedB] = pairKey(studentIdA, studentIdB);
      const [answerOfSortedA, answerOfSortedB] =
        sortedA === studentIdA ? [dataA.answer, dataB.answer] : [dataB.answer, dataA.answer];
      const detail: TaskSimilarityDetail = {
        taskNumber: task.taskNumber,
        taskType: task.taskType,
        studentAAnswer: answerOfSortedA.slice(0, MAX_STORED_ANSWER_CHARS),
        studentBAnswer: answerOfSortedB.slice(0, MAX_STORED_ANSWER_CHARS),
        rawSimilarity,
        surprisalWeight,
        wrongAnswerBoost,
        weightedScore,
        // Only a SHARED wrong answer is evidence; two different wrong answers are just two weak students. And only
        // an uncommon one: a misconception a fifth of the class shares says nothing about this pair.
        isWrongForBoth:
          wrongAnswerBoost > 1 && rawSimilarity >= NEAR_IDENTICAL_SIMILARITY && !isSharedByMany(COMMON_MISTAKE_SHARE),
        isVerbatim,
      };
      // Leave-one-out with one pseudo-count: of the others, how many gave this answer — not counting the partner, whose
      // match is what's under test — so an answer only this pair gave is 1/(n−1), and everyone's answer is 1.
      const chance: ChanceMatch = {
        isSameAnswer,
        probabilityA: (clusterSizeA - (isSameAnswer ? 1 : 0)) / (totalStudents - 1),
        probabilityB: (clusterSizeB - (isSameAnswer ? 1 : 0)) / (totalStudents - 1),
      };
      contributions.push({ studentIdA: sortedA, studentIdB: sortedB, rawSimilarity, weightedScore, detail, chance });
    }
  }
  return contributions;
}

// ---------------------------------------------------------------------------
// Full-exam orchestration
// ---------------------------------------------------------------------------

interface ExamLeafTask {
  pathStr: string;
  taskNumber: string;
  taskType: TaskType;
  declaredMaxPoints: number | null;
}

function collectAnswerableLeafTasks(exam: any): { tasks: ExamLeafTask[]; excludedTaskNumbers: string[] } {
  const tasks: ExamLeafTask[] = [];
  const excludedTaskNumbers: string[] = [];
  for (const { pathStr, node } of flattenExamTasks(exam)) {
    const taskType = node?.taskType as TaskType | undefined;
    if (!taskType || taskType === TaskType.TASK_FRAME) continue; // structural node, never itself answerable
    if (node?.needsAnswer === false) continue; // explicitly not an answer-bearing task
    if (classifyTaskComparisonMode(taskType) === 'unsupported') {
      excludedTaskNumbers.push(pathStr);
      continue;
    }
    tasks.push({
      pathStr,
      // The full dotted path, not the leaf's own raw taskNumber: a subtask's bare number ("b") is ambiguous
      // and can collide with an identically-numbered subtask under a different parent — "4.b" is what a
      // teacher needs to actually find the task, and what the UI displays as "Aufgabe {{taskNumber}}".
      taskNumber: pathStr,
      taskType,
      declaredMaxPoints: typeof node?.declaredMaxPoints === 'number' ? node.declaredMaxPoints : null,
    });
  }
  return { tasks, excludedTaskNumbers };
}

/** submissionId -> task path -> answer text, for answers that aren't in `studentAnswer` (see copy-detection-answers.ts). */
export type AnswerTextOverrides = Map<string, Map<string, string>>;

/** submissionId -> task path -> that submission's task node, flattened once per submission rather than per task. */
function indexSubmissionTasks(submissions: any[]): Map<string, Map<string, any>> {
  const nodesBySubmission = new Map<string, Map<string, any>>();
  for (const submission of submissions) {
    // Identified by the SUBMISSION's own _id, not `studentID`: that FK is written only by the (separate,
    // manual) result-distribution step and is still null for most/all submissions at the moment grading
    // finishes — which is exactly when this job runs. `_id` is always present and is also what the Summary
    // canvas already keys its own per-student rows by (see `use-summary-state.ts`: `id: sub._id`), so the UI
    // can resolve a display name for a pair without any extra roster lookup.
    const submissionId = submission?._id ? String(submission._id) : null;
    if (!submissionId) continue;
    const nodeByPath = new Map<string, any>();
    for (const { pathStr, node } of flattenSubmissionTasks(submission)) {
      if (!nodeByPath.has(pathStr)) nodeByPath.set(pathStr, node);
    }
    nodesBySubmission.set(submissionId, nodeByPath);
  }
  return nodesBySubmission;
}

function buildAnswersByStudent(
  nodesBySubmission: Map<string, Map<string, any>>,
  pathStr: string,
  answerTexts?: AnswerTextOverrides,
): Map<string, StudentTaskAnswer> {
  const answersByStudent = new Map<string, StudentTaskAnswer>();
  for (const [submissionId, nodeByPath] of nodesBySubmission) {
    const node = nodeByPath.get(pathStr);
    const answer =
      answerTexts?.get(submissionId)?.get(pathStr) ??
      (typeof node?.studentAnswer === 'string' ? node.studentAnswer : null);
    const actualPointsGiven =
      typeof node?.gradingResult?.actualPointsGiven === 'number' ? node.gradingResult.actualPointsGiven : null;
    answersByStudent.set(submissionId, { answer, actualPointsGiven });
  }
  return answersByStudent;
}

function computeMean(values: number[]): number {
  return values.length === 0 ? 0 : values.reduce((sum, value) => sum + value, 0) / values.length;
}

function computeStdDev(values: number[], mean: number): number {
  if (values.length === 0) return 0;
  const variance = computeMean(values.map(value => (value - mean) ** 2));
  return Math.sqrt(variance);
}

function computeMedian(values: number[]): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 1 ? (sorted[mid] as number) : ((sorted[mid - 1] as number) + (sorted[mid] as number)) / 2;
}

/** Scale for a robust z-score: MAD (normal-consistent), falling back to mean absolute deviation when more than
 *  half the pairs share the median score. Mean/stddev would let the outliers inflate their own yardstick — with
 *  several copy pairs in one class, each one hides the others. */
function computeRobustScale(values: number[], median: number): number {
  const deviations = values.map(value => Math.abs(value - median));
  const mad = computeMedian(deviations);
  if (mad > 0) return 1.4826 * mad;
  return 1.2533 * computeMean(deviations);
}

interface PairAggregate {
  studentIdA: string;
  studentIdB: string;
  scoreSum: number;
  taskCount: number;
  nearIdenticalCount: number;
  details: TaskSimilarityDetail[];
  chances: ChanceMatch[];
}

/** Resolution of the significance test's surprisal sums: fine enough not to matter, coarse enough to stay cheap. */
const SURPRISAL_STEP = 0.01;

/** How likely a student working alone matches the other at least this improbably: every task can match by chance
 *  with its probability p and then adds its surprisal −log(p). Exact over the tasks (a Poisson-binomial over
 *  surprisal sums, in `SURPRISAL_STEP`s), conditioned on one side's answers. */
function chanceOfMatching(tasks: { isSameAnswer: boolean; probability: number }[]): number {
  const weights = tasks.map(({ probability }) => Math.round(-Math.log(probability) / SURPRISAL_STEP));
  const observed = tasks.reduce(
    (sum, { isSameAnswer }, index) => sum + (isSameAnswer ? (weights[index] as number) : 0),
    0,
  );
  if (observed === 0) return 1;
  // below[k]: probability of a chance sum of exactly k steps; reaching the observed sum is absorbed into `atLeast`.
  let below = new Float64Array(observed);
  below[0] = 1;
  let atLeast = 0;
  tasks.forEach(({ probability }, index) => {
    const weight = weights[index] as number;
    if (weight === 0) return;
    const next = new Float64Array(observed);
    for (let sum = 0; sum < observed; sum++) {
      const mass = below[sum] as number;
      if (mass === 0) continue;
      next[sum] = (next[sum] as number) + mass * (1 - probability);
      if (sum + weight >= observed) atLeast += mass * probability;
      else next[sum + weight] = (next[sum + weight] as number) + mass * probability;
    }
    below = next;
  });
  return atLeast;
}

/** Symmetric: conditioned on either student's answers, whichever makes the match look less improbable. */
function chanceOfPairMatching(chances: ChanceMatch[]): number {
  return Math.max(
    chanceOfMatching(chances.map(({ isSameAnswer, probabilityA }) => ({ isSameAnswer, probability: probabilityA }))),
    chanceOfMatching(chances.map(({ isSameAnswer, probabilityB }) => ({ isSameAnswer, probability: probabilityB }))),
  );
}

/** Absolute rules first: they hold regardless of class size, where the statistical rule is blind (one pair has no
 *  distribution, three pairs can't produce a z above 1.41). */
function resolveFlagReason(
  pair: PairAggregate,
  overallScore: number,
  zScore: number,
  submissionSimilarity: number | null,
  pairCount: number,
  opts: Required<CopyDetectionOptions>,
): CopyFlagReason | null {
  const sharedMistakeCount = pair.details.filter(detail => detail.isWrongForBoth).length;
  const sharesMistake = sharedMistakeCount > 0;
  // Correct maths working is formulaic, so two strong students can write much of an exam alike. Below
  // near-verbatim, a whole-submission match only counts together with a shared mistake.
  if (
    submissionSimilarity !== null &&
    (submissionSimilarity >= opts.submissionSimilarityThreshold ||
      (submissionSimilarity >= opts.submissionSimilarityWithMistakeThreshold && sharesMistake))
  ) {
    return 'identicalSubmission';
  }
  if (pair.details.some(detail => detail.isVerbatim)) return 'verbatimText';
  // Shared mistakes are the evidence — all-identical CORRECT answers are what two strong students produce too.
  // 90% rather than all: across a dozen subtasks, one transcription that came out differently must not hide a copy.
  if (
    pair.taskCount >= opts.identicalMinTasks &&
    pair.nearIdenticalCount / pair.taskCount >= IDENTICAL_TASK_SHARE &&
    (sharedMistakeCount >= IDENTICAL_MIN_SHARED_MISTAKES ||
      (sharesMistake && chanceOfPairMatching(pair.chances) <= IDENTICAL_SINGLE_MISTAKE_CHANCE))
  ) {
    return 'identicalAnswers';
  }
  // An outlier among this class's pairs isn't yet evidence: pair scores are skewed, and in a few hundred pairs some
  // share a handful of semi-rare answers by chance. It must also be improbable for two students working alone,
  // across every pair of the exam (Bonferroni) — checked last, as it's the one costly test.
  if (
    pairCount >= opts.statisticalMinPairs &&
    overallScore > 0 &&
    zScore >= opts.zScoreThreshold &&
    chanceOfPairMatching(pair.chances) * pairCount <= opts.statisticalSignificance
  ) {
    return 'statistical';
  }
  return null;
}

/**
 * Builds the full copy-detection result for one exam: scores every pair of students on every comparable task,
 * aggregates into one overall score per pair, and flags pairs on absolute evidence (verbatim free text, identical
 * answers including shared mistakes) or as robust statistical outliers within this exam's own distribution that
 * students working alone would match only by improbable chance.
 */
export function buildCopyDetectionResult(
  exam: { _id: string; tasks?: any[] },
  submissions: any[],
  options: CopyDetectionOptions = {},
  answerTexts?: AnswerTextOverrides,
): CopyDetectionResult {
  const opts = { ...DEFAULT_OPTIONS, ...options };
  const { tasks, excludedTaskNumbers } = collectAnswerableLeafTasks(exam);
  const taskOrder = new Map(tasks.map((task, index) => [task.taskNumber, index]));

  const aggregateByPair = new Map<string, PairAggregate>();
  // Per-task comparison can't see a copy whose individual answers are each too short or too canonical to prove
  // anything — but a whole exam's worth of worked solutions written identically is.
  const writtenAnswersBySubmission = new Map<string, string[]>();

  const checkedTaskNumbers: string[] = [];
  const nodesBySubmission = indexSubmissionTasks(submissions);

  for (const task of tasks) {
    const answersByStudent = buildAnswersByStudent(nodesBySubmission, task.pathStr, answerTexts);
    // A subtask without text from at least two submissions (blank, or an image-only crop whose transcription
    // failed) is reported as unchecked, never as clean.
    const transcribedCount = [...answersByStudent.values()].filter(({ answer }) => hasAnswer(answer)).length;
    if (transcribedCount < 2) {
      excludedTaskNumbers.push(task.pathStr);
      continue;
    }
    checkedTaskNumbers.push(task.taskNumber);
    // A one-value task's answer can run long — a transcribed checklist lists every option, a gap text every gap —
    // but it reads the same for every student who got it right.
    const isWrittenOut =
      classifyTaskComparisonMode(task.taskType) !== 'discrete' || task.taskType === TaskType.SHORT_TEXT;
    for (const [submissionId, { answer }] of answersByStudent) {
      if (!isWrittenOut || !hasAnswer(answer) || tokenize(answer).length < WRITTEN_ANSWER_MIN_TOKENS) continue;
      const written = writtenAnswersBySubmission.get(submissionId) ?? [];
      written.push(answer);
      writtenAnswersBySubmission.set(submissionId, written);
    }
    const contributions = computeTaskPairwiseScores(
      { taskNumber: task.taskNumber, taskType: task.taskType, declaredMaxPoints: task.declaredMaxPoints },
      answersByStudent,
      opts,
    );
    for (const contribution of contributions) {
      const key = `${contribution.studentIdA}|${contribution.studentIdB}`;
      const existing = aggregateByPair.get(key) ?? {
        studentIdA: contribution.studentIdA,
        studentIdB: contribution.studentIdB,
        scoreSum: 0,
        taskCount: 0,
        nearIdenticalCount: 0,
        details: [],
        chances: [],
      };
      existing.scoreSum += contribution.weightedScore;
      existing.taskCount += 1;
      if (contribution.rawSimilarity >= NEAR_IDENTICAL_SIMILARITY) existing.nearIdenticalCount += 1;
      existing.details.push(contribution.detail);
      existing.chances.push(contribution.chance);
      aggregateByPair.set(key, existing);
    }
  }

  const overallScores = [...aggregateByPair.values()].map(pair => pair.scoreSum / pair.taskCount);
  const meanScore = computeMean(overallScores);
  const stdDevScore = computeStdDev(overallScores, meanScore);
  const medianScore = computeMedian(overallScores);
  const robustScale = computeRobustScale(overallScores, medianScore);
  const pairCount = aggregateByPair.size;

  // Tokenized once per submission, not once per pair it's in. Trigrams most of the class wrote — a dictated model
  // answer, the task statement copied out, the one way to write a standard step — are dropped, for the same reason a
  // per-task verbatim match skips common material: they make two students alike without either having copied.
  const allShinglesBySubmission = new Map<string, Set<string>>();
  for (const [submissionId, answers] of writtenAnswersBySubmission) {
    allShinglesBySubmission.set(submissionId, shingles(tokenize(answers.join(' '))));
  }
  const writerCountByShingle = new Map<string, number>();
  for (const submissionShingles of allShinglesBySubmission.values()) {
    for (const shingle of submissionShingles) {
      writerCountByShingle.set(shingle, (writerCountByShingle.get(shingle) ?? 0) + 1);
    }
  }
  const isCommonShingle = (shingle: string) => {
    const writerCount = writerCountByShingle.get(shingle) ?? 0;
    return (
      writerCount >= COMMON_MATERIAL_MIN_STUDENTS && writerCount / allShinglesBySubmission.size >= COMMON_MATERIAL_SHARE
    );
  };
  const writtenShinglesBySubmission = new Map<string, Set<string>>();
  for (const [submissionId, submissionShingles] of allShinglesBySubmission) {
    const ownShingles = new Set([...submissionShingles].filter(shingle => !isCommonShingle(shingle)));
    // A run of N words holds N − 2 trigrams.
    if (ownShingles.size >= opts.submissionMinTokens - (SHINGLE_SIZE - 1)) {
      writtenShinglesBySubmission.set(submissionId, ownShingles);
    }
  }
  const computeSubmissionSimilarity = (submissionIdA: string, submissionIdB: string): number | null => {
    const shinglesA = writtenShinglesBySubmission.get(submissionIdA);
    const shinglesB = writtenShinglesBySubmission.get(submissionIdB);
    return shinglesA && shinglesB ? jaccard(shinglesA, shinglesB) : null;
  };

  const pairs: PairwiseCopyScore[] = [...aggregateByPair.values()].map(pair => {
    const overallScore = pair.scoreSum / pair.taskCount;
    const zScore = robustScale === 0 ? 0 : (overallScore - medianScore) / robustScale;
    const submissionSimilarity = computeSubmissionSimilarity(pair.studentIdA, pair.studentIdB);
    const flagReason = resolveFlagReason(pair, overallScore, zScore, submissionSimilarity, pairCount, opts);
    return {
      studentIdA: pair.studentIdA,
      studentIdB: pair.studentIdB,
      overallScore,
      taskCount: pair.taskCount,
      // Every compared subtask, in exam order, but only where the UI shows it: one row per subtask for every pair
      // of a 30-student class would outgrow the result document.
      perTask:
        flagReason === null
          ? []
          : pair.details.sort((a, b) => (taskOrder.get(a.taskNumber) ?? 0) - (taskOrder.get(b.taskNumber) ?? 0)),
      zScore,
      submissionSimilarity,
      flagged: flagReason !== null,
      flagReason,
    };
  });
  // Absolute evidence first, then by score — the UI lists flagged pairs in this order.
  const REASON_RANK: CopyFlagReason[] = ['identicalSubmission', 'verbatimText', 'identicalAnswers', 'statistical'];
  const reasonRank = (reason: CopyFlagReason | null) => (reason ? REASON_RANK.indexOf(reason) : REASON_RANK.length);
  pairs.sort((a, b) => reasonRank(a.flagReason) - reasonRank(b.flagReason) || b.overallScore - a.overallScore);

  return {
    examId: String(exam._id),
    computedAt: new Date().toISOString(),
    submissionCount: submissions.length,
    pairs,
    flaggedPairIds: pairs.filter(pair => pair.flagged).map(pair => `${pair.studentIdA}|${pair.studentIdB}`),
    meanScore,
    stdDevScore,
    excludedTaskNumbers,
    checkedTaskNumbers,
  };
}
